import React, { useState, useCallback, useEffect } from 'react';
import texasApi from '../../services/texasApi';
import { landlordApi } from '../../services/api';

export const EvidenceHub: React.FC = () => {
  const [evidenceTypes, setEvidenceTypes] = useState<any[]>([]);
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedProperty, setSelectedProperty] = useState<string>('');
  const [selectedType, setSelectedType] = useState<string>('');
  const [fileName, setFileName] = useState<string>('');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [typesRes, itemsRes] = await Promise.allSettled([
        landlordApi.get('api/evidence/types'),
        landlordApi.get('api/evidence'),
      ]);
      if (typesRes.status === 'fulfilled') {
        const d = typesRes.value?.data;
        if (Array.isArray(d)) setEvidenceTypes(d);
        else if (Array.isArray(d?.data)) setEvidenceTypes(d.data);
      }
      if (itemsRes.status === 'fulfilled') {
        const d = itemsRes.value?.data;
        let list: any[] = [];
        if (Array.isArray(d)) list = d;
        else if (Array.isArray(d?.data)) list = d.data;
        else if (Array.isArray(d?.items)) list = d.items;
        setItems(list);
      }
      if (typesRes.status === 'rejected' && itemsRes.status === 'rejected') {
        const reason: any = itemsRes.reason;
        throw new Error(reason?.message || 'Failed to load evidence');
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to load evidence');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleUpload = async () => {
    if (!selectedProperty || !selectedType || !fileName) return;
    setLoading(true);
    setError(null);
    try {
      const res = await landlordApi.post('api/evidence/presign-upload', {
        propertyId: selectedProperty,
        evidenceType: selectedType,
        fileName,
        contentType: 'application/pdf',
        fileSizeBytes: 0,
        sha256Checksum: '',
        retentionYears: 7,
        metadata: {},
      });
      const presignData = res?.data?.data || res?.data;
      if (presignData?.uploadUrl) {
        await landlordApi.put(presignData.uploadUrl, {}, {
          headers: { 'Content-Type': 'application/pdf' },
        });
        await landlordApi.post('api/evidence/confirm-upload', {
          propertyId: selectedProperty,
          evidenceId: presignData.evidenceId,
          sha256Checksum: '',
          fileSizeBytes: 0,
          metadata: {},
        });
      }
      await loadData();
      setFileName('');
    } catch (err: any) {
      setError(err?.message || 'Upload failed');
    } finally {
      setLoading(false);
    }
  };

  const properties = items.length > 0 ? [...new Set(items.map((i: any) => i.propertyId || i.propertyName))] : [];

  return (
    <div data-testid="evidence-hub" className="texas-page-container">
      <h1>Evidence Hub</h1>
      <p className="page-description">
        Compliance evidence vault — upload, track, and package evidence for Texas properties.
      </p>

      {error && <div className="error" data-testid="error">{error}</div>}

      <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 mb-4">
        <h2 className="text-lg font-bold mb-3">Upload Evidence</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
          <select
            data-testid="property-select"
            value={selectedProperty}
            onChange={(e) => setSelectedProperty(e.target.value)}
            className="p-2 border rounded text-sm"
          >
            <option value="">Select Property</option>
            {properties.map((p: string) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
          <select
            data-testid="type-select"
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="p-2 border rounded text-sm"
          >
            <option value="">Select Type</option>
            {evidenceTypes.map((t: any) => (
              <option key={t.type || t} value={t.type || t}>{t.type || t}</option>
            ))}
          </select>
          <input
            type="text"
            placeholder="File Name"
            value={fileName}
            onChange={(e) => setFileName(e.target.value)}
            data-testid="file-name-input"
            className="p-2 border rounded text-sm"
          />
        </div>
        <button
          onClick={handleUpload}
          disabled={loading || !selectedProperty || !selectedType || !fileName}
          data-testid="upload-evidence-btn"
          className="px-4 py-2 bg-emerald-600 text-white rounded text-sm hover:bg-emerald-700 disabled:opacity-50"
        >
          {loading ? 'Uploading...' : 'Upload Evidence'}
        </button>
      </section>

      <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <h2>Evidence Items ({items.length})</h2>
        {loading ? (
          <div className="no-results">Loading evidence...</div>
        ) : items.length === 0 ? (
          <div className="no-results">No evidence items found</div>
        ) : (
          <div className="space-y-2">
            {items.map((item: any) => (
              <div key={item.id || item.evidenceId} className="p-3 bg-gray-50 rounded-lg border border-gray-200/80 flex items-center justify-between">
                <div>
                  <div className="font-bold text-gray-900 text-xs">{item.fileName || item.evidenceType || 'Unknown'}</div>
                  <div className="text-xs text-slate-500">{item.propertyId || item.propertyName || '—'} — {item.evidenceType || '—'}</div>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                  item.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-800'
                }`}>
                  {item.status || 'pending'}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default EvidenceHub;
